/**
 * Header component with explanation and filter controls
 */
import React, { useState } from "react"
import { Select, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCircleQuestion,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons"
import { LabelType } from "utils/labelUtils"

const HELP_OPEN_KEY = "metadataManagerHelpOpen"

const { Option } = Select

interface LabelsHeaderProps {
  filterType: string
  filteredCount: number
  onFilterChange: (value: string) => void
  /** Which label types to offer in the filter (defaults to all) */
  availableTypes?: LabelType[]
}

/** Display label for each filterable type */
const TYPE_OPTION_LABELS: Record<LabelType, string> = {
  [LabelType.QUESTION]: "Question Labels",
  [LabelType.LIST]: "Lists",
  [LabelType.RANGE_LIST]: "Range Lists",
  [LabelType.EXCEPTIONS]: "Exceptions",
}

export const LabelsHeader: React.FC<LabelsHeaderProps> = ({
  filterType,
  filteredCount,
  onFilterChange,
  availableTypes,
}) => {
  const typesToShow =
    availableTypes ??
    ([
      LabelType.QUESTION,
      LabelType.LIST,
      LabelType.RANGE_LIST,
      LabelType.EXCEPTIONS,
    ] as LabelType[])
  const showQuestionInfo = typesToShow.includes(LabelType.QUESTION)

  const [helpOpen, setHelpOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(HELP_OPEN_KEY) === "true"
    } catch {
      return false
    }
  })

  const toggleHelp = (): void => {
    setHelpOpen((prev) => {
      const next = !prev
      try {
        localStorage.setItem(HELP_OPEN_KEY, String(next))
      } catch {
        /* ignore */
      }
      return next
    })
  }

  return (
    <>
      {/* Collapsible explanation section */}
      <div className="mb-3 bg-blue-50 border border-blue-200 rounded">
        <button
          type="button"
          onClick={toggleHelp}
          aria-expanded={helpOpen}
          className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-blue-900"
        >
          <span>How to use this manager</span>
          <FontAwesomeIcon
            icon={helpOpen ? faChevronDown : faChevronRight}
            className="text-xs"
          />
        </button>
        {helpOpen && (
          <div className="px-3 pb-3">
            <ul className="text-xs text-blue-800 space-y-1 ml-4 list-disc">
              {showQuestionInfo && (
                <li>
                  <strong>Question Labels</strong>: Edit the{" "}
                  <strong>Label</strong> (the variable name) to rename it — every
                  occurrence updates automatically, including references — and
                  edit the <strong>Question text</strong> to change the wording.
                </li>
              )}
              <li>
                <strong>Lists</strong>: Edit content to rename or modify list
                items. When you rename a list, all references (PER/FROM) are
                updated automatically
              </li>
              <li>
                <strong>Range Lists</strong>: Edit the range definition (e.g.,
                "RANGE LIST Scale: 1..5")
              </li>
              <li>
                <strong>References</strong>: Shows how many times a list is used
                in questions. Click line numbers to jump to that location in the
                editor
              </li>
            </ul>
            <p className="text-xs text-blue-700 mt-2 italic">
              After editing, click "Update Questionnaire" to apply changes, then
              save your questionnaire.
            </p>
          </div>
        )}
      </div>

      {/* Filter Controls */}
      <div className="flex items-center mb-4 justify-between">
        <div className="flex items-center">
          <span className="mr-2">Filter by Type:</span>
          <Select
            value={filterType}
            onChange={onFilterChange}
            style={{ width: 200 }}
          >
            <Option value="ALL">All Types</Option>
            {typesToShow.map((type) => (
              <Option key={type} value={type}>
                {TYPE_OPTION_LABELS[type]}
              </Option>
            ))}
          </Select>
        </div>
        <div className="flex items-center">
          <Tooltip title="Edit labels and lists, then click 'Update Questionnaire' to apply changes to all occurrences.">
            <FontAwesomeIcon
              icon={faCircleQuestion}
              className="text-[var(--dq-brand)] h-4 mr-2"
            />
          </Tooltip>
          <span className="text-sm text-gray-600">
            {filteredCount} item(s) found
          </span>
        </div>
      </div>
    </>
  )
}
